import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, RefreshCw, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useBookings } from '../hooks/useAdmin';
import AdminHeader from '../components/AdminHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBookings = () => {
  const navigate = useNavigate();
  const { bookings, loading: bookingsLoading, refetch: refetchBookings, invalidateCache: invalidateBookingsCache } = useBookings();
  const [calendarData, setCalendarData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Force scroll to top on mount
    window.scrollTo(0, 0);
    
    const init = async () => {
      await checkAuth();
      await fetchCalendar();
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line

  const checkAuth = async () => {
    const token = localStorage.getItem('admin_token');
    console.log('AdminBookings - Checking auth, token exists:', !!token);
    
    if (!token) {
      console.log('AdminBookings - No token found, redirecting to login');
      navigate('/login', { replace: true });
      return false;
    }
    
    try {
      const response = await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('AdminBookings - Token valid:', response.data);
      return true;
    } catch (error) {
      console.log('AdminBookings - Token invalid, removing and redirecting');
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_token_time');
      navigate('/login', { replace: true });
      return false;
    }
  };

  const fetchCalendar = async () => {
    const token = localStorage.getItem('admin_token');
    try {
      const response = await axios.get(`${API}/bookings/calendar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCalendarData(response.data);
    } catch (error) {
      console.error('Error fetching calendar:', error);
    }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    const token = localStorage.getItem('admin_token');
    try {
      await axios.patch(`${API}/bookings/${bookingId}`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Status erfolgreich aktualisiert!");
      invalidateBookingsCache();
      await refetchBookings(true);
      await fetchCalendar();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Möchten Sie diese Buchung wirklich löschen?')) {
      return;
    }

    const token = localStorage.getItem('admin_token');
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Buchung erfolgreich gelöscht!");
      invalidateBookingsCache();
      await refetchBookings(true);
      await fetchCalendar();
    } catch (error) {
      console.error('Error deleting booking:', error);
      toast.error("Fehler beim Löschen");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'completed':
        return 'bg-blue-500';
      case 'cancelled':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Aktiv';
      case 'completed':
        return 'Abgeschlossen';
      case 'cancelled':
        return 'Storniert';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader title="Buchungskalender" subtitle="Buchungsverwaltung" />

      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#2C3E50]">Buchungskalender</h1>
            <p className="text-gray-600 mt-2">Übersicht aller aktiven und abgeschlossenen Buchungen</p>
          </div>
          <Button 
            onClick={async () => { await refetchBookings(true); await fetchCalendar(); }} 
            variant="outline" 
            disabled={bookingsLoading}
            className="w-full sm:w-auto"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${bookingsLoading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
        </div>

        <Card className="mb-8 border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl text-slate-800">
              <Calendar className="h-6 w-6 text-yellow-600" />
              Mietkalender - Aktive Buchungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            {calendarData.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Keine aktiven Buchungen</p>
            ) : (
              <div className="space-y-3">
                {calendarData.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border-l-4 border-yellow-500">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800">{item.vehicle_name}</p>
                      <p className="text-sm text-gray-600">{item.customer}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{formatDate(item.start)} - {formatDate(item.end)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <div className="text-center py-12">
            <RefreshCw className="h-12 w-12 animate-spin mx-auto text-yellow-600 mb-4" />
            <p className="text-gray-600">Lade Buchungen...</p>
          </div>
        ) : bookings.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg">Noch keine Buchungen vorhanden</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {bookings.map((booking) => (
              <Card key={booking.id} className="border-2 hover:border-yellow-500 transition-all">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-xl text-slate-800">{booking.vehicle_name}</CardTitle>
                      <p className="text-gray-600 mt-1">{booking.customer_name}</p>
                    </div>
                    <Badge className={`${getStatusColor(booking.status)} text-white`}>
                      {getStatusText(booking.status)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Kontakt</p>
                      <p className="font-medium">{booking.customer_email}</p>
                      <p className="font-medium">{booking.customer_phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Mietdauer</p>
                      <p className="font-medium">{formatDate(booking.start_date)} bis {formatDate(booking.end_date)}</p>
                    </div>
                  </div>
                  
                  {booking.notes && (
                    <div className="mt-4 p-3 bg-gray-50 rounded">
                      <p className="text-sm text-gray-500 mb-1">Notizen:</p>
                      <p className="text-gray-700">{booking.notes}</p>
                    </div>
                  )}

                  <div className="flex gap-3 mt-4 flex-wrap">
                    {booking.status === 'active' && (
                      <>
                        <Button 
                          onClick={() => updateBookingStatus(booking.id, 'completed')}
                          className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Als abgeschlossen markieren
                        </Button>
                        <Button 
                          onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                          variant="outline"
                          className="border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white"
                        >
                          <XCircle className="mr-2 h-4 w-4" />
                          Stornieren
                        </Button>
                      </>
                    )}
                    <Button 
                      onClick={() => handleDeleteBooking(booking.id)}
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Löschen
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBookings;
